/* Hydrogen HomeSwiper: same Swiper 11.1.15 engine, loop and configurable autoplay (10s default).
 * Liquid owns resource links/settings. Responsive sources must never upscale
 * the portrait mobile MP4 across a landscape desktop viewport.
 * Deliberate improvements: scoped video lifecycle, reduced motion, and a
 * keyboard-accessible pause control. No native cart/menu/form handlers touched.
 */
(() => {
  'use strict';
  const instances = new Map();
  const mobile = window.matchMedia('(max-width: 767px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function init(root) {
    if (instances.has(root)) return;
    const track = root.querySelector('[data-lk-swiper]');
    if (!track) return;
    const slides = Array.from(track.querySelectorAll('[data-lk-slide]'));
    if (!slides.length) return;
    const controller = new AbortController();
    const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, {...options, signal: controller.signal});
    const pause = root.querySelector('[data-lk-pause]');
    const canAutoplay = root.closest('[data-lk-home]')?.hasAttribute('data-lk-autoplay');
    let userPaused = !canAutoplay || reducedMotion.matches;
    let focused = false;
    let visible = true;
    let current = 0;
    let swiper;
    let observer;
    let scrollFrame;

    const stopped = () => userPaused || focused || !visible || document.hidden || reducedMotion.matches;
    function sourceFor(video) {
      return mobile.matches
        ? (video.dataset.mobileFailed === 'true' ? video.dataset.mobileFallbackSrc : video.dataset.mobileSrc || video.dataset.mobileFallbackSrc)
        : video.dataset.desktopSrc;
    }
    function updateVideos() {
      slides.forEach((slide, index) => {
        const video = slide.querySelector('[data-lk-responsive-video]');
        if (!video) return;
        if (index !== current || stopped()) {
          video.pause();
          return;
        }
        const source = sourceFor(video);
        const poster = mobile.matches ? video.dataset.mobilePoster : video.dataset.desktopPoster;
        if (poster) video.poster = poster;
        if (!source) return; // Do not substitute a portrait source on desktop.
        if (video.getAttribute('src') !== source) {
          video.classList.remove('is-playing');
          video.src = source;
          video.preload = 'metadata';
          video.load();
        }
        video.muted = true;
        video.play()?.catch(() => {}); // Responsive poster stays visible.
      });
    }
    function update() {
      current = swiper ? swiper.realIndex : current;
      if (pause) {
        pause.setAttribute('aria-pressed', String(userPaused));
        pause.setAttribute('aria-label', userPaused ? 'Play slideshow and video' : 'Pause slideshow and video');
        pause.textContent = userPaused ? 'Play slideshow' : 'Pause slideshow';
      }
      if (swiper?.autoplay) {
        if (stopped()) swiper.autoplay.stop();
        else if (slides.length > 1 && !swiper.autoplay.running) swiper.autoplay.start();
      }
      updateVideos();
    }

    slides.forEach((slide) => {
      const video = slide.querySelector('[data-lk-responsive-video]');
      if (!video) return;
      listen(video, 'playing', () => video.classList.add('is-playing'));
      listen(video, 'error', () => {
        video.classList.remove('is-playing');
        const fallback = video.dataset.mobileFallbackSrc;
        if (mobile.matches && fallback && video.getAttribute('src') !== fallback) {
          video.dataset.mobileFailed = 'true';
          video.src = fallback;
          video.load();
          if (!stopped()) video.play()?.catch(() => {});
        }
      });
    });

    if (typeof window.Swiper === 'function') {
      swiper = new window.Swiper(track, {
        loop: slides.length > 1,
        speed: reducedMotion.matches ? 0 : 300,
        autoplay: canAutoplay && !reducedMotion.matches
          // Each slide overrides this fallback with data-swiper-autoplay.
          ? {delay: 10000, disableOnInteraction: false}
          : false,
        on: {
          slideChange: update,
          transitionEnd: update,
        },
      });
    } else {
      // CDN/library failure: retain a native, manually scrollable slide rail.
      // Never claim this fallback has Swiper's seamless loop behavior.
      track.classList.add('is-native-fallback');
      const wrapper = track.querySelector('.swiper-wrapper');
      listen(wrapper, 'scroll', () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
          const left = wrapper.getBoundingClientRect().left;
          current = slides.reduce((best, slide, index) =>
            Math.abs(slide.getBoundingClientRect().left - left) < Math.abs(slides[best].getBoundingClientRect().left - left) ? index : best, 0);
          update();
        });
      }, {passive: true});
    }

    if (pause) {
      pause.hidden = false;
      listen(pause, 'click', () => { userPaused = !userPaused; update(); });
    }
    listen(root, 'focusin', () => { focused = true; update(); });
    listen(root, 'focusout', (event) => { focused = root.contains(event.relatedTarget); update(); });
    listen(document, 'visibilitychange', update);
    listen(mobile, 'change', update);
    listen(reducedMotion, 'change', () => {
      if (reducedMotion.matches) userPaused = true;
      if (swiper) swiper.params.speed = reducedMotion.matches ? 0 : 300;
      update();
    });
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; update(); }, {threshold: .1});
      observer.observe(root);
    }
    update();

    instances.set(root, {swiper, dispose() {
      controller.abort();
      observer?.disconnect();
      cancelAnimationFrame(scrollFrame);
      slides.forEach((slide) => slide.querySelector('video')?.pause());
      swiper?.destroy(true, true);
      instances.delete(root);
    }});
  }
  const mount = (scope) => scope.querySelectorAll('[data-lk-home-carousel]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), {once: true});
  else mount(document);
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance, root) => { if (event.target.contains(root)) instance.dispose(); });
  });
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-lk-slide]');
    if (!slide) return;
    const root = slide.closest('[data-lk-home-carousel]');
    const swiper = instances.get(root)?.swiper;
    const index = Number(slide.dataset.swiperSlideIndex);
    if (swiper && Number.isFinite(index)) swiper.slideToLoop(index);
  });
})();